<script setup lang="ts">
import { ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import ProgressSpinner from 'primevue/progressspinner'
import GbButton from '@/components/GbButton.vue'
import { betaOptIn } from '@/util/beta_opt_in'
import AdminErrorToasts from './AdminErrorToasts.vue'
import { createAnchorPage } from './anchor_page_editor_api'
import { useAdminErrors } from './use_admin_errors'

const route = useRoute()
const router = useRouter()

const title = ref('')
const isCreating = ref(false)
const createdGradeLevelUuid = ref<string>()
const { errors, notifyError, dismissError } = useAdminErrors()

async function create() {
  if (!title.value) return
  isCreating.value = true
  try {
    createdGradeLevelUuid.value ??= await createAnchorPage(title.value)
    const navigationFailure = await router.push({ name: 'EditAnchorAccessPoints', params: { uuid: createdGradeLevelUuid.value }, query: betaOptIn(route.query) })
    if (navigationFailure) notifyError('Could not open the new anchor page', navigationFailure)
  } catch (error) {
    console.error(error)
    notifyError('Could not create anchor page', error)
  } finally {
    isCreating.value = false
  }
}
</script>

<template>
  <div class="container py-6">
    <h1 class="mb-4 header-lg">Create Anchor Page with Access Points</h1>
    <form @submit.prevent="create">
      <div class="mb-4">
        <label for="anchor-title" class="block mb-1 font-bold">Title</label>
        <input
          id="anchor-title"
          v-model="title"
          type="text"
          placeholder="Title for new anchor page with access points"
          class="w-full h-10 px-3 text-md !font-regular text-gray-900 bg-white border border-gray-600 rounded-xs placeholder:text-gray-700 focus:outline-primary-400"
        >
      </div>
      <GbButton type="submit" gbTheme="bluePrimary" :disabled="!title || isCreating">
        <template v-if="isCreating">
          <ProgressSpinner class="h-4 w-4 mr-2" aria-hidden="true" /> Creating...
        </template>
        <template v-else>Create</template>
      </GbButton>
    </form>
    <AdminErrorToasts :errors="errors" @dismiss="dismissError" />
  </div>
</template>
